import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Music2, ChevronLeft, ChevronRight, Headphones } from 'lucide-react';
import api from '../../lib/api';

/**
 * Horizontal scrolling carousel of DJ profiles. Circular portraits to
 * differentiate from event cards. Includes left/right arrow controls on
 * desktop and native swipe on mobile.
 */
export default function DJsOrbit() {
  const [djs, setDjs] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/public/djs?limit=20');
        if (mounted) setDjs(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (mounted) setDjs([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-2" data-testid="djs-orbit-loading">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex-shrink-0 w-48 h-72 rounded-2xl bg-white/[0.03] animate-pulse" />
        ))}
      </div>
    );
  }
  if (djs.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-white/10 rounded-2xl" data-testid="djs-orbit-empty">
        <Headphones className="mx-auto text-white/20 mb-3" size={32} />
        <p className="text-white/40 text-sm">DJs en construcción.</p>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="djs-orbit">
      {/* Desktop arrow controls */}
      <div className="hidden md:flex absolute -top-14 right-0 gap-2">
        <button
          type="button"
          onClick={() => scroll(-1)}
          className="w-10 h-10 rounded-full border border-white/15 bg-white/[0.03] hover:bg-white/10 text-white/80 flex items-center justify-center transition-colors"
          aria-label="Anterior"
          data-testid="djs-prev"
        >
          <ChevronLeft size={18} />
        </button>
        <button
          type="button"
          onClick={() => scroll(1)}
          className="w-10 h-10 rounded-full border border-white/15 bg-white/[0.03] hover:bg-white/10 text-white/80 flex items-center justify-center transition-colors"
          aria-label="Siguiente"
          data-testid="djs-next"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-4 sm:gap-5 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-white/10"
        style={{ scrollbarWidth: 'thin' }}
      >
        {djs.map((dj, idx) => (
          <DJCard key={dj.id} dj={dj} delay={idx * 0.04} />
        ))}
      </div>
    </div>
  );
}

function DJCard({ dj, delay = 0 }) {
  const accent = '#D4AF37';
  const genres = (dj.genres || []).slice(0, 2);
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
      className="snap-start flex-shrink-0 w-44 sm:w-52"
    >
      <Link
        to={`/dj/${dj.slug || dj.id}`}
        className="group block"
        data-testid={`dj-orbit-${dj.id}`}
      >
        {/* Circular portrait with glow ring */}
        <div className="relative">
          <div className="aspect-square rounded-full overflow-hidden bg-[#0A0A0A] border border-white/10 group-hover:border-[#D4AF37]/60 transition-colors duration-500">
            {dj.photo ? (
              <img
                src={dj.photo}
                alt={dj.name}
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.04] transition-all duration-700"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/20">
                <Music2 size={32} />
              </div>
            )}
          </div>
          {/* Number badge */}
          <div
            className="absolute -top-1 -right-1 w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-black border-2 border-[#050505]"
            style={{ background: accent, color: '#000', fontFamily: "'Unbounded', sans-serif" }}
          >
            DJ
          </div>
        </div>

        {/* Name + genres */}
        <div className="mt-4 px-1 text-center">
          <h4
            className="text-base sm:text-lg font-extrabold text-white truncate group-hover:text-[#D4AF37] transition-colors"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
          >
            {dj.name}
          </h4>
          {genres.length > 0 && (
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 mt-1 truncate">
              {genres.join(' · ')}
            </p>
          )}
        </div>
      </Link>
    </motion.div>
  );
}
